/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { Loading } from 'carbon-components-react';
import React from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
  isFeatureEnabled,
  selectIsGenesysAudioConnectorInCpd
} from '@ibm-watson/shared/modules/ia/store/features/selectors';
import { useAppSelector } from '@ibm-watson/shared/store';
import { useChannelForm } from '../../hooks/useChannelForm';
import { useInitializeSpeech } from '../../hooks/useInitializeSpeech';
import {
  selectIsIntegrationInitializing,
  isIntegrationLoading
} from '../../store/channels/selectors';
import { selectPhoneIntegrationType } from '../../store/phone/selectors';
import {
  GENESYS_AUDIO_CONNECTOR_TYPE,
  PHONE_CHANNEL_TYPE
} from '../../util/channelTypes';
import { INTEGRATIONS_FEATURE_FLAGS } from '../../util/integrationsUtil';
import { ChannelProps } from '../../util/types';
import { TabbedHeader } from '../common/tabbed-header/TabbedHeader';
import { AdvancedOptions } from './advanced-tab/AdvancedOptions';
import { ConnectionsTab } from './connections-tab/ConnectionsTab';
import { Connections } from './connections-tab/messages';
import { Phone as messages } from './messages';
import { PhoneSplashPage } from './new/PhoneSplashPage';
import { PhoneNumberTab } from './phone-number-tab/PhoneNumberTab';
import {
  phoneSettingsSchema,
  phoneSoeSettingsSchema,
  phoneGenesysAudioConnectorSchema,
  phoneGenesysAudioConnectorSchemaInCpd
} from './schema';
import { SpeechToText } from './speech-to-text-tab/messages';
import { SpeechToTextTab } from './speech-to-text-tab/SpeechToTextTab';
import { TextToSpeech } from './text-to-speech-tab/messages';
import { TextToSpeechTab } from './text-to-speech-tab/TextToSpeechTab';
import './Phone.scss';

export const Phone = ({
  assistantId,
  environmentId,
  integrationId,
  isNewClassicIntegration,
  updateClassicIntegrationOverlay
}: ChannelProps) => {
  // Speech is initialized for brand new phone integrations.
  // We show a waiting state until the phone integration's required
  // speech to text and text to speech settings have been set AND
  // the integration has been properly updated/is no longer dirty.
  // The !isNewClassicIntegration prevents the loading to occur in
  // Classic IA when Tanya is on the phone splash page.
  const initializingSpeech = useAppSelector(selectIsIntegrationInitializing);

  // Below will give which contact center aka Connections type is selected
  const contactCenterProvider = useAppSelector(selectPhoneIntegrationType);

  const loadingIntegration = useAppSelector(isIntegrationLoading);
  const soeFeatureEnabled = useAppSelector(state =>
    isFeatureEnabled(state, INTEGRATIONS_FEATURE_FLAGS.SOE_ENABLED)
  );

  const isGenesysAudioConnectorEnabledInCpd = useAppSelector(
    selectIsGenesysAudioConnectorInCpd
  );

  // In case of Genesys audio connector it has a different integrations schema structure
  // Below will check if the contactCenterProvider is GenesysAudioConnector and select settings schema
  // based on it
  const isGenesysAudioConnectorSelected =
    contactCenterProvider === GENESYS_AUDIO_CONNECTOR_TYPE;

  const settingsSchema = soeFeatureEnabled
    ? phoneSoeSettingsSchema
    : isGenesysAudioConnectorEnabledInCpd
    ? phoneGenesysAudioConnectorSchemaInCpd
    : isGenesysAudioConnectorSelected
    ? phoneGenesysAudioConnectorSchema
    : phoneSettingsSchema;

  const {
    loading,
    initialName,
    updateIntegrationName,
    formMethods,
    isReadOnly
  } = useChannelForm(
    PHONE_CHANNEL_TYPE,
    assistantId,
    environmentId,
    integrationId,
    settingsSchema
  );
  useInitializeSpeech(
    assistantId,
    environmentId,
    formMethods,
    updateClassicIntegrationOverlay
  );

  // Definition for when the Phone component is still loading:
  // - New integrations in classic go through the splash page and do
  //   not need a Loading overlay
  // - The phone integration is still being loaded, OR
  // - The speech instances are still being loaded

  const newPhoneLoading = isNewClassicIntegration
    ? false
    : loadingIntegration || initializingSpeech;

  const { formatMessage: t } = useIntl();

  const tabs = [
    <ConnectionsTab key="tab-1" tabname={t(Connections.connections)} />,
    // Need to hide Phone number tab in case of Genesys Audio Connector
    !isGenesysAudioConnectorSelected && (
      <PhoneNumberTab
        key="tab-2"
        tabname={t(messages.phoneNumber)}
        readOnly={isReadOnly}
      />
    ),
    <SpeechToTextTab key="tab-3" tabname={t(SpeechToText.title)} />,
    <TextToSpeechTab key="tab-4" tabname={t(TextToSpeech.title)} />,
    // Need to hide AdvancedOptions tab in case of Genesys Audio Connector
    !isGenesysAudioConnectorSelected && (
      <AdvancedOptions
        key="tab-5"
        tabname={t(messages.advancedOptions)}
        showSOE={soeFeatureEnabled}
      />
    )
  ];

  return (
    <FormProvider {...formMethods}>
      {newPhoneLoading ? (
        <Loading />
      ) : (
        <TabbedHeader
          assistantId={assistantId}
          environmentId={environmentId}
          channelType={PHONE_CHANNEL_TYPE}
          integrationName={initialName}
          updateIntegrationName={updateIntegrationName}
          useSplashPage={isNewClassicIntegration}
          loading={loading}
          SplashPage={<PhoneSplashPage />}
          Tabs={tabs}
          readOnly={isReadOnly}
        />
      )}
    </FormProvider>
  );
};

export default Phone;
